import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

import { APP_VERSION } from '@/constants';
import {
  registerNotificationToken,
  unregisterNotificationToken,
  type NotificationDeviceType,
} from '@/api/notifications';
import { toast } from '@/stores/toast';

/**
 * Push notification service (Firebase Cloud Messaging via
 * @capacitor-firebase/messaging — returns an FCM token on BOTH platforms;
 * iOS APNS→FCM mapping is handled by the Firebase SDK).
 *
 * Native-only v1: plain-browser Web Push (VAPID + service worker) is out of
 * scope — on `web` every entry point no-ops silently.
 */

export const getDeviceType = (
  platform: string = Capacitor.getPlatform(),
): NotificationDeviceType => {
  if (platform === 'ios') return 'ios';
  if (platform === 'android') return 'android';
  return 'web';
};

export const canUsePush = (platform: string = Capacitor.getPlatform()): boolean =>
  platform === 'ios' || platform === 'android';

let lastToken: string | null = null;
let registering = false;

async function registerWithBackend(token: string): Promise<void> {
  await registerNotificationToken({
    token,
    device_type: getDeviceType(),
    app_version: APP_VERSION,
  });
  lastToken = token;
}

export type PushPermissionStatus = 'granted' | 'denied' | 'prompt';

/** Current OS-level permission (null when push is unavailable, i.e. web). */
export async function getPushPermissionStatus(): Promise<PushPermissionStatus | null> {
  if (!canUsePush()) return null;
  try {
    const { receive } = await FirebaseMessaging.checkPermissions();
    if (receive === 'granted') return 'granted';
    if (receive === 'denied') return 'denied';
    return 'prompt'; // 'prompt' | 'prompt-with-rationale'
  } catch (error) {
    console.warn('[push] permission check failed:', error);
    return null;
  }
}

async function acquireAndRegisterToken(): Promise<boolean> {
  const { token } = await FirebaseMessaging.getToken();
  if (!token) return false;
  await registerWithBackend(token);
  return true;
}

/**
 * SILENT registration — called on every app start / login so `last_used`
 * stays fresh. Never fires the OS permission prompt: the first-time soft-ask
 * modal (NotificationPermissionModal) owns that moment via registerForPush().
 */
export async function initPushNotifications(): Promise<boolean> {
  if (!canUsePush() || registering) return false;
  registering = true;
  try {
    const { receive } = await FirebaseMessaging.checkPermissions();
    if (receive !== 'granted') return false;
    return await acquireAndRegisterToken();
  } catch (error) {
    // Push must never break the app — log and move on.
    console.warn('[push] init failed:', error);
    return false;
  } finally {
    registering = false;
  }
}

/**
 * EXPLICIT opt-in — fires the OS permission prompt (once per OS lifetime),
 * then registers the token. Call from the soft-ask modal's accept button or
 * the Profile "activate" CTA — never automatically.
 */
export async function registerForPush(): Promise<boolean> {
  if (!canUsePush() || registering) return false;
  registering = true;
  try {
    let permission = await FirebaseMessaging.checkPermissions();
    if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
      permission = await FirebaseMessaging.requestPermissions();
    }
    if (permission.receive !== 'granted') return false;
    return await acquireAndRegisterToken();
  } catch (error) {
    console.warn('[push] opt-in failed:', error);
    return false;
  } finally {
    registering = false;
  }
}

/** Best-effort cleanup on logout: unregister server-side, drop the local token. */
export async function teardownPushNotifications(): Promise<void> {
  if (!canUsePush()) return;
  try {
    const token = lastToken ?? (await FirebaseMessaging.getToken()).token;
    if (token) await unregisterNotificationToken(token);
  } catch (error) {
    console.warn('[push] unregister failed:', error);
  }
  try {
    await FirebaseMessaging.deleteToken();
  } catch {
    /* token may not exist — fine */
  }
  lastToken = null;
}

export interface PushListenerHandlers {
  /** A2 — the user tapped the notification: route to the matching screen. */
  onTap?: (data: Record<string, string>) => void;
  /**
   * A8 — a payload arrived (foreground receive OR tap): server state changed,
   * so the caches it touches must be refreshed. Fires for BOTH events, because
   * a push received while the app is open is exactly the case where the stale
   * screen is already in front of the user.
   */
  onData?: (data: Record<string, string>) => void;
}

/**
 * Runtime listeners: token rotation re-registers with the backend; a push
 * received in the foreground surfaces as a toast (payload is pre-localized by
 * the backend) and reports its `data` to `onData`; a tap on a notification
 * (warm or cold start — the plugin retains the launch event until a listener
 * attaches) hands the payload's `data` to both `onTap` (routing, A2) and
 * `onData` (cache invalidation, A8). Returns a cleanup function.
 */
export function addPushListeners(handlers: PushListenerHandlers = {}): () => void {
  if (!canUsePush()) return () => {};

  const { onTap, onData } = handlers;

  const handles = [
    FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
      if (token && token !== lastToken) {
        void registerWithBackend(token).catch((error) =>
          console.warn('[push] token refresh registration failed:', error),
        );
      }
    }),
    FirebaseMessaging.addListener('notificationReceived', ({ notification }) => {
      if (notification?.title) toast.success(notification.title);
      // A8 — the payload used to stop here: `data` was dropped and nothing in
      // the app ever refreshed.
      onData?.((notification?.data ?? {}) as Record<string, string>);
    }),
    FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => {
      const data = (notification?.data ?? {}) as Record<string, string>;
      onData?.(data);
      onTap?.(data);
    }),
  ];

  return () => {
    handles.forEach((h) => void h.then((l) => l.remove()).catch(() => {}));
  };
}
